import { inject, injectable } from "tsyringe";
import { catchError, map, tap } from "rxjs/operators";
import { throwError } from "rxjs";
import { PromotickTokenMapping } from "src/domains/wellbeing/mappings/token";
import type {
    IWellbeingRepository,
    IWellbeingService,
} from "src/domains/wellbeing/types";
import { CouldNotGetPromotickToken } from "src/domains/wellbeing/errors/could-not-get-promotick-token";
import { CookieStorageService } from "src/shared/services/cookie-storage/cookie-storage-service";
import type { PromotickToken } from "src/domains/wellbeing/models/token";
import type { UserSegment } from "src/domains/wellbeing/models/segment";
import { SegmentMapping } from "src/domains/wellbeing/mappings/segment";

@injectable()
class WellbeingService implements IWellbeingService {
    static key = "WellbeingService";
    public readonly tokenStorageKey = "PROMOTICK_AUTH_TOKEN";

    constructor(
        @inject("WellbeingRepository") private repository: IWellbeingRepository,
        @inject("CookieStorageService")
        private cookieStorage: CookieStorageService
    ) {}

    getToken = (catalog: number): Promise<PromotickToken> => {
        // Pick<Class, keyof Class> se usa para obtener
        // una representación json plana de una clase
        // a partir de sus campos.
        const promotickToken = this.cookieStorage.getCookie<
            Pick<PromotickToken, keyof PromotickToken>
        >(this.tokenStorageKey);

        if (promotickToken) {
            return Promise.resolve(
                PromotickTokenMapping.jsonToToken({
                    token: promotickToken,
                })
            );
        }

        return this.repository
            .getToken(catalog)
            .pipe(
                tap(({ token, exp }) => {
                    this.cookieStorage.setCookie(
                        this.tokenStorageKey,
                        token,
                        new Date(exp)
                    );
                }),
                map(PromotickTokenMapping.jsonToToken),
                catchError((err) =>
                    throwError(new CouldNotGetPromotickToken(err))
                )
            )
            .toPromise();
    };

    getUserSegment = (): Promise<UserSegment> => {
        return this.repository
            .getUserSegment()
            .pipe(map(SegmentMapping.jsonToSegment))
            .toPromise();
    };

    hasAccess(): Promise<boolean> {
        return this.repository.hasAccess().toPromise();
    }
}

export { WellbeingService };
